import type { SkiScoredDay, SurfScoredDay } from "@/lib/conditions/detail";
import { skiLabel10 } from "@/lib/conditions/ski-labels-v3";
import { skiV3ForForecastDay, skiV3IsClosed } from "@/lib/conditions/ski-v3-carriers";
import type { NullableHourlyScore } from "@/lib/conditions/session-window-universal";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import {
  namedSupForecastSafetyState,
  selectNamedSupBestForecastDate,
} from "@/lib/conditions/named-sup-forecast";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import {
  skiScoreConfidenceMetadata,
  skiScoreConfidenceIsPublishable,
  type SkiDepthGatedSport,
} from "@/lib/conditions/ski-score-confidence";

type StoredMountainScore = {
  score: number;
  label: string;
};

type CompositeSurfHour = {
  hour: number;
  compositeScore: number;
  isDark: boolean;
};

type CoastalHourlyFallback = {
  hours: CompositeSurfHour[];
  bestHour?: { hour: number } | null;
} | null;

type CoastalTodayTimelineInput = {
  activeSport: string;
  sportHourlyScores: Record<string, NullableHourlyScore[]>;
  hourlyData: CoastalHourlyFallback;
};

type CoastalTodayTimeline = {
  scores: NullableHourlyScore[];
  sport: string;
  bestHour?: number | null;
};

export function selectCoastalOutlookForecast({
  activeSport,
  sportForecast,
  surfForecast,
}: {
  activeSport: string;
  sportForecast: SurfScoredDay[] | null | undefined;
  surfForecast: SurfScoredDay[];
}): SurfScoredDay[] {
  if (activeSport === "surf") return surfForecast;
  if (sportForecast && sportForecast.length > 0) return sportForecast;
  return [];
}

export function coastalOutlookSafetyState(
  activeSport: string,
  day: SurfScoredDay,
): ConditionSafetyState | undefined {
  return isSupProfileKey(activeSport)
    ? namedSupForecastSafetyState(day, activeSport)
    : day.safetyState?.[activeSport];
}

export function selectCoastalOutlookBestDate({
  activeSport,
  forecast,
  surfBestDate: _surfBestDate,
}: {
  activeSport: string;
  forecast: SurfScoredDay[];
  surfBestDate: string | null;
}): string | null {
  const eligible = forecast.filter((day) => {
    const state = coastalOutlookSafetyState(activeSport, day);
    return state !== "flagged-red" && state !== "no-data";
  });
  if (isSupProfileKey(activeSport)) {
    return selectNamedSupBestForecastDate(eligible, activeSport);
  }
  return eligible.reduce<SurfScoredDay | null>(
    (best, day) => !best || day.score > best.score ? day : best,
    null,
  )?.date ?? null;
}

function storedMountainForecastScore(
  day: SkiScoredDay,
  sport: SkiDepthGatedSport,
): StoredMountainScore | null {
  const stored = day.sportScores?.[sport];
  if (typeof stored !== "number" || Number.isNaN(stored)) return null;
  const score = Math.round(stored * 10) / 10;
  // One vocabulary per sport (consumer inventory Section 7): piste, or the natural one.
  return { score, label: skiLabel10(sport, score) };
}

export function buildMountainForecastDays(
  skiForecast: SkiScoredDay[],
  activeSport: string,
): SkiScoredDay[] {
  const depthSport: SkiDepthGatedSport | null = activeSport === "piste" ||
      activeSport === "off-piste" ||
      activeSport === "touring"
    ? activeSport
    : null;
  if (!depthSport) return skiForecast;

  const stormSnowCodes = [71, 73, 75, 77, 85, 86];
  return skiForecast.map((day) => {
    const depthConfidence = skiScoreConfidenceMetadata(day, depthSport);
    // Ski v3 fields for the active sport (status, class, P10 and P90, bands, time blocks).
    const v3 = skiV3ForForecastDay(day)[depthSport] ?? {};
    if (skiV3IsClosed(v3, depthSport)) {
      // Closed: no number, "Closed" (or "No lift access"); the value stays detail only.
      return {
        ...day,
        ...v3,
        label: skiLabel10(depthSport, null, v3.status, v3.statusReason),
        scoreAvailable: false,
        snowDepthConfidence: depthConfidence,
      };
    }
    const stored = storedMountainForecastScore(day, depthSport);
    if (!stored) {
      return {
        ...day,
        ...v3,
        // A v3 day with no stored score reads "Score unavailable" (never the row's piste
        // label, which may say "Closed" for another sport). Revision 2 days keep their label.
        ...(v3.cls ? { label: skiLabel10(depthSport, null, v3.status, v3.statusReason) } : {}),
        scoreAvailable: false,
        snowDepthConfidence: depthConfidence,
      };
    }
    const assumesTrees = activeSport === "off-piste"
      && (day.treelineTerrain === "extensive" || day.treelineTerrain === "limited")
      && day.snowfallCm >= 5
      && stormSnowCodes.includes(day.weatherCode);

    return {
      ...day,
      ...v3,
      ...stored,
      scoreAvailable: skiScoreConfidenceIsPublishable(depthConfidence.confidence),
      snowDepthConfidence: depthConfidence,
      assumesTrees,
    };
  });
}

export function buildCoastalTodayTimeline({
  activeSport,
  sportHourlyScores,
  hourlyData,
}: CoastalTodayTimelineInput): CoastalTodayTimeline | null {
  if (activeSport === "surf") {
    const storedSurfScores = sportHourlyScores.surf;
    if (storedSurfScores) {
      return {
        scores: storedSurfScores,
        sport: "surf",
        bestHour: hourlyData?.bestHour?.hour ?? null,
      };
    }

    if (hourlyData) {
      return {
        scores: hourlyData.hours.map((h) => ({
          hour: h.hour,
          score: h.compositeScore / 10,
          isDay: !h.isDark,
        })),
        sport: "surf",
        bestHour: hourlyData.bestHour?.hour ?? null,
      };
    }
  }

  const scores = sportHourlyScores[activeSport];
  if (!scores) return null;

  return {
    scores,
    sport: activeSport,
  };
}
